import { cn } from '@/lib/utils';

export interface ScoreGaugeProps {
  score: number | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  max?: number;
  className?: string;
}

const SIZE_MAP = {
  sm: { px: 48, stroke: 4, fontSize: 10, labelSize: 8 },
  md: { px: 96, stroke: 6, fontSize: 18, labelSize: 11 },
  lg: { px: 160, stroke: 10, fontSize: 32, labelSize: 14 },
};

function getArcColor(pct: number): string {
  if (pct >= 0.75) return '#4CAF7D'; // emerald  (≥750/1000 or ≥75/100)
  if (pct >= 0.50) return '#38BDF8'; // sky      (≥500/1000 or ≥50/100)
  if (pct >= 0.25) return '#D4A843'; // amber    (≥250/1000 or ≥25/100)
  return '#D95F72';                  // rose     (<250/1000 or <25/100)
}

// Gauge spans 260° starting at bottom-left (-220° from SVG default 3-o'clock start)
const START_ANGLE = -220;
const SWEEP_DEG = 260;

/**
 * Speedometer-style gauge.
 * Opens at the bottom; arc runs from bottom-left through top to bottom-right.
 * Uses strokeDasharray/strokeDashoffset on <circle> elements — both circles
 * share the same cx/cy/r and transform so they are perfectly aligned.
 */
export function ScoreGauge({ score, size = 'md', showLabel = true, max = 100, className }: ScoreGaugeProps) {
  const { px, stroke, fontSize, labelSize } = SIZE_MAP[size];
  const r = (px - stroke) / 2;
  const cx = px / 2;
  const cy = px / 2;

  const pct = score != null ? Math.max(0, Math.min(score / max, 1)) : 0;
  const color = getArcColor(pct);
  const displayScore = score != null ? Math.round(score) : null;

  const circumference = 2 * Math.PI * r;
  const gaugeArc = (SWEEP_DEG / 360) * circumference;

  // Track: show gaugeArc length, gap covers the rest (full circumference is a safe large gap)
  const trackDash = `${gaugeArc} ${circumference}`;
  // Value: show proportional portion of gaugeArc, gap covers the rest
  const valueDash = `${pct * gaugeArc} ${circumference}`;

  // The arc endpoints dip below cy by sin(40°)·r (the gap is 100° centred at the bottom,
  // so each endpoint is 40° below the horizontal through cy).
  const endpointY = cy + Math.sin((40 * Math.PI) / 180) * r + stroke / 2;
  const svgHeight = endpointY + (showLabel && size !== 'sm' ? labelSize + 6 : 0);

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <svg
        width={px}
        height={svgHeight}
        viewBox={`0 0 ${px} ${endpointY}`}
        style={{ overflow: 'visible' }}
        aria-label={displayScore != null ? `Score: ${displayScore} out of ${max}` : 'No score'}
      >
        {/* Gray track — full 260° arc */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#1E2E42"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={trackDash}
          transform={`rotate(${START_ANGLE}, ${cx}, ${cy})`}
        />

        {/* Colored value arc — same origin, same rotation */}
        {pct > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={valueDash}
            transform={`rotate(${START_ANGLE}, ${cx}, ${cy})`}
          />
        )}

        {/* Score label — centred inside the horseshoe */}
        {showLabel && (
          <text
            x={cx}
            y={cy + 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={fontSize}
            fontWeight="700"
            fontFamily="JetBrains Mono, monospace"
            fill={score != null ? color : '#8DA0B8'}
          >
            {displayScore ?? '—'}
          </text>
        )}

        {/* "/ max" sub-label */}
        {showLabel && size !== 'sm' && (
          <text
            x={cx}
            y={cy + fontSize / 2 + 8}
            textAnchor="middle"
            fontSize={labelSize}
            fill="#8DA0B8"
          >
            / {max}
          </text>
        )}
      </svg>
    </div>
  );
}
